












































































































import gql from 'graphql-tag';
import Vue, { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { BAlert } from 'bootstrap-vue';
import { ZenFormGroup } from 'zenComponents';
import ZenMultiselect from '@reciprocity/multiselect';
import ZenButton from '@reciprocity/button';
import { isEmpty, get, cloneDeep } from 'lodash';
import GenericCard from '@/components/cards/GenericCard.vue';
import { ModalSize } from '@/components/zen_modal/types';
import { VisualizationOptions, VISUALIZATIONS } from '@/constants/DashboardConstants';
import RichText from '@/components/rich_text/RichText.vue';
import { Alert } from '@/components/global_alerts/types';
import { DropdownOption } from '@/components/cards/types';
import { DashboardBlock, DashboardTitleErrors, DimensionType } from './types';
import DashboardCardTitle, { DashboardCardTitleType } from './DashboardCardTitle.vue';
import DashboardBlockSetup from './components/DashboardBlockSetup.vue';
import DashboardBlockFilters from './components/DashboardBlockFilters.vue';
import DashboardBlockPreview from './components/DashboardBlockPreview.vue';
import { DASHBOARD_QUERY } from '@/pages/dashboards/Dashboard.vue';
import { mapBlockSettings } from '@/utils/DashboardUtils';
import { isObjectAttributeValueSelected, isMappedObjectAttributeValueSelected } from './components/SeriesUtils';

const CREATE_BLOCK_MUTATION = gql`
  mutation CreateBlock($input: CreateDashboardBlockInput!) {
    result: createDashboardBlock(input: $input) {
      block: result {
        id
        nodeId: id
        dbId
        title {
          value
        }
        settings {
          value
        }
        textContent {
          value
        }
      }
    }
  }
`;

const UPDATE_BLOCK_MUTATION = gql`
  mutation UpdateBlock($id: ID!, $input: UpdateDashboardBlockInput!) {
    result: updateDashboardBlock(id: $id, input: $input) {
      block: result {
        id
        nodeId: id
        title {
          value
        }
        settings {
          value
        }
        textContent {
          value
        }
      }
    }
  }
`;

const DELETE_BLOCK_MUTATION = gql`
  mutation DeleteDashboardBlock($id: ID!) {
    result: deleteDashboardBlock(id: $id) {
      removed
    }
  }
`;

export default Vue.extend({
  name: 'DashboardBlockCard',
  components: {
    GenericCard,
    ZenFormGroup,
    ZenMultiselect,
    ZenButton,
    BAlert,
    DashboardCardTitle,
    DashboardBlockSetup,
    DashboardBlockFilters,
    DashboardBlockPreview,
    RichText,
  },
  props: {
    cardTitle: {
      type: String,
      required: true,
    },
    block: {
      type: Object as PropType<DashboardBlock>,
      default: () => {},
    },
    dashboardId: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  apollo: {
    ownerGroupId: {
      query: gql`
        query GetCurrentUserGroupId {
          currentUser {
            id
            privateGroup {
              value {
                id
              }
            }
          }
        }
      `,
      update(result) {
        // TODO: Remove after ac_groups is enabled
        this.currentUserId = get(result, 'currentUser.id');
        return get(result, 'currentUser.privateGroup.value.id');
      },
    },
  },
  data() {
    return {
      title: <string>'',
      selectedDimension: <DropdownOption>{},
      selectedMetric: <DropdownOption>{},
      selectedVisualization: <DropdownOption>{},
      selectedMappedObjectAttr: <DropdownOption>{},
      mappedObjects: <any[]>[],
      showFilterByDimension: false,
      showFilterByObject: false,
      saving: false,
      alerts: <Alert[]>[],
      ownerGroupId: null,
      currentUserId: null,
      dashboardBlockTitleType: DashboardCardTitleType.DASHBOARD_BLOCK,
      titleErrors: <DashboardTitleErrors>{
        titleExists: false,
        titleTooLong: false,
      },
      filters: [],
      currentMappedObjects: [],
      changedSelectedMetric: '',
      textContent: '',
    };
  },
  computed: {
    ...mapGetters(['featureFlags']),
    isRichText() {
      return this.selectedVisualization.value === VISUALIZATIONS.RICH_TEXT;
    },
    isEdit() {
      return !!this.block;
    },
    blockId() {
      return this.isEdit ? this.block.id : null;
    },
    okTitle() {
      return this.isEdit ? 'Save' : 'Add';
    },
    visualizations() {
      if (this.featureFlags.dashboards_rich_text) {
        return Object.entries(VisualizationOptions).map(([key, options]) => ({ value: key, label: options.label }));
      } else {
        return Object.entries(VisualizationOptions)
          .filter(([key]) => key !== 'RICH_TEXT')
          .map(([key, options]) => ({ value: key, label: options.label }));
      }
    },
    mutationQuery() {
      return this.isEdit ? UPDATE_BLOCK_MUTATION : CREATE_BLOCK_MUTATION;
    },
  },
  async created() {
    this.changeModalSize(this.featureFlags.dashboard_block_preview ? ModalSize.Large : ModalSize.Medium);

    if (this.block) {
      const {
        settings: { showFilterByDimension, showFilterByObject },
      } = this.block;

      this.title = this.block.title;

      this.selectedVisualization = this.visualizations.find(
        visualization => visualization.value === this.block.settings.visualization
      );

      this.showFilterByDimension = showFilterByDimension;
      this.showFilterByObject = showFilterByObject;

      this.filters = !isEmpty(this.block.settings.filters) ? cloneDeep(this.block.settings.filters) : [];

      this.textContent = this.block.textContent;
    }
  },
  methods: {
    ...mapActions({
      changeModalSize: 'modals/changeSize',
      cancelModal: 'modals/cancel',
      confirmModal: 'modals/confirm',
      showModal: 'modals/show',
      showAlert: 'alerts/show',
    }),
    async save() {
      if (this.isValid()) {
        this.saving = true;

        // TODO: remove after ac_groups is enabled
        const owners = this.featureFlags.ac_groups
          ? {
              ownersGroup: [this.ownerGroupId],
            }
          : {
              owners: [this.currentUserId],
            };

        const variables = {
          input: {
            title: this.title.trim(),
            dashboard: this.dashboardId,
            textContent: this.textContent,
            ...owners,
            ...(!this.isEdit && { layout: JSON.stringify({ x: 0, y: 0, w: 1, h: 1 }) }),
            // TODO: Figure out why we need to do this for JSON fields
            settings: JSON.stringify(this.blockSettings()),
          },
          ...(this.isEdit && { id: this.blockId }),
        };

        try {
          const response = await this.$apollo.mutate({
            mutation: this.mutationQuery,
            variables,
            refetchQueries: [
              {
                query: DASHBOARD_QUERY,
                variables: {
                  dashboardId: this.dashboardId,
                },
              },
            ],
          });

          const block = get(response, 'data.result.block');

          this.confirmModal(block);
        } catch {
          this.showAlert({
            type: 'error',
            message: `Failed ${this.isEdit ? 'updating existing' : 'adding new'} Block`,
          });
        } finally {
          this.saving = false;
        }
      }
    },
    blockSettings() {
      return mapBlockSettings(
        this.selectedVisualization,
        this.selectedMetric,
        this.selectedDimension,
        this.selectedMappedObjectAttr,
        this.filters
      );
    },
    async confirmRemoving() {
      try {
        await this.showModal({
          type: 'DashboardDeleteCard',
          data: {
            dashboardElement: this.title ? this.title : 'Block',
          },
        });
        this.remove();
      } catch {
        // User closed modal
      }
    },
    async remove() {
      const variables = {
        id: this.block.nodeId,
      };
      try {
        const result = await this.$apollo.mutate({
          mutation: DELETE_BLOCK_MUTATION,
          variables,
          refetchQueries: [
            {
              query: DASHBOARD_QUERY,
              variables: {
                dashboardId: this.dashboardId,
              },
            },
          ],
        });
        this.confirmModal(get(result, 'data.result'));
      } catch {
        this.showAlert({
          type: 'error',
          message: 'Failed removing Block',
        });
      }
    },
    isValid() {
      const fields = this.isRichText
        ? {
            Title: this.title.trim(),
            'Chart Type': !isEmpty(this.selectedVisualization),
            'Rich Text': !isEmpty(this.textContent),
          }
        : {
            Title: this.title.trim(),
            'Chart Type': !isEmpty(this.selectedVisualization),
            'Object Type': !isEmpty(this.selectedMetric),
            'Attribute Type': !isEmpty(this.selectedDimension),
            Attribute: !(
              this.selectedDimension.type === DimensionType.MAPPING && isEmpty(this.selectedMappedObjectAttr)
            ),
            [`${this.selectedMetric.label} Attribute Value`]: isObjectAttributeValueSelected(this.filters),
            [`${this.selectedMetric.label} Mapped Object Attribute Value`]: isMappedObjectAttributeValueSelected(
              this.filters
            ),
          };

      this.alerts = Object.keys(fields)
        .filter(f => !fields[f])
        .map(f => this.getRequiredError(f));

      if (this.titleErrors.titleExists) {
        this.alerts.push({
          type: 'error',
          message: 'Title already exists.',
        });
      }

      if (this.titleErrors.titleTooLong) {
        this.alerts.push({
          type: 'error',
          message: 'Title too long.',
        });
      }

      return !this.alerts.length;
    },
    getRequiredError(label: string) {
      return {
        type: 'error',
        message: `Field ${label} is required.`,
      };
    },
  },
});
